import type { To } from 'react-router-dom';

const DEFAULT_FALLBACK_PATH = '/';

function isUnsafePathString(trimmed: string): boolean {
  return trimmed.startsWith('//') || trimmed.includes('\\') || /^[\s]*(https?:)/i.test(trimmed);
}

function tryParseInternalPath(path: string): string | null {
  if (!path || typeof path !== 'string') {
    return null;
  }

  const trimmed = path.trim();

  if (!trimmed || isUnsafePathString(trimmed)) {
    return null;
  }

  // React Router child-route segments (e.g. SubPaths.MATERIALS) are relative paths.
  if (!trimmed.startsWith('/')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed, 'http://internal.local');
    if (parsed.origin !== 'http://internal.local') {
      return null;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/**
 * Returns a same-origin relative path safe for in-app navigation.
 * Blocks open redirects (protocol-relative URLs, absolute URLs, backslashes).
 */
export function sanitizeInternalPath(path: string, fallback: string = DEFAULT_FALLBACK_PATH): string {
  const safeFallback = tryParseInternalPath(fallback) ?? DEFAULT_FALLBACK_PATH;
  return tryParseInternalPath(path) ?? safeFallback;
}

/** Sanitizes react-router `To` targets before `navigate()`. */
export function sanitizeNavigateTarget(to: To, fallback: string = DEFAULT_FALLBACK_PATH): To {
  const safeFallback = sanitizeInternalPath(fallback);

  if (typeof to === 'string') {
    return sanitizeInternalPath(to, safeFallback);
  }

  if (typeof to === 'object' && to !== null) {
    if (to.pathname != null) {
      return { ...to, pathname: sanitizeInternalPath(to.pathname, safeFallback) };
    }

    return to;
  }

  return safeFallback;
}
